import { Chips } from '@/ui/Chips';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { NecesitaConexion } from '@/ui/NecesitaConexion';
import { EncabezadoPantalla, EncabezadoSeccion } from '@/ui/Sistema';
import { useEffect, useState, type FormEvent } from 'react';
import { listParticipants, type Participant } from '@/features/works/works';
import { inviteUser } from './organizations';
import { ParticipantesCard } from './ParticipantesCard';
import { useOrg } from './OrgProvider';

/**
 * Alta de invitaciones. Solo la ve un administrador, pero la restricción real
 * la aplica RLS: `organization_invitations` exige `app.is_org_admin`.
 * Ocultar el formulario es ergonomía, no seguridad.
 */
export function TeamPage() {
  const { activeOrg } = useOrg();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  /** A quién corresponde en el reparto. '' = todavía no está en el reparto. */
  const [participante, setParticipante] = useState('');
  const [libres, setLibres] = useState<Participant[]>([]);
  const [recarga, setRecarga] = useState(0);
  // Ni verde ni rojo: la invitación sirve, el aviso no salió.
  const [aviso, setAviso] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Los participantes que todavía NO tienen usuario. Los que ya lo tienen no se
  // ofrecen: nadie puede ser dos personas, y ofrecerlo sería prometer algo que
  // la base rechaza.
  const orgId = activeOrg?.organization_id;
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const l = await listParticipants(orgId);
        if (!cancelado) setLibres(l.filter((p) => p.kind === 'person' && p.member_id === null));
      } catch {
        // En silencio: sin la lista se puede invitar igual y vincular después.
        // Bloquear la invitación por esto sería peor que no ofrecer el campo.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, recarga]);

  // Invitar, cambiar roles y tocar participantes son operaciones contra el
  // servidor, y ninguna tiene sentido con datos viejos: quién es administrador
  // hoy lo sabe él. La copia local sirve para NO ofrecer lo que no corresponde,
  // nunca para autorizar.
  if (useSinConexion()) {
    return (
      <div className="flex flex-col gap-5 p-4">
        <NecesitaConexion
          que="Quiénes están en el equipo y qué permisos tienen se consulta al servidor."
          testId="equipo-sin-conexion"
        />
      </div>
    );
  }

  if (!activeOrg) return null;

  if (!activeOrg.is_admin) {
    return (
      <p className="superficie p-4 text-sm text-[var(--tinta-suave)]" data-testid="team-denied">
        La gestión del equipo está reservada a administradores.
      </p>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setAviso(null);
    setError(null);
    try {
      const r = await inviteUser({
        organizationId: activeOrg.organization_id,
        email,
        role,
        participantId: participante === '' ? null : participante,
      });
      // Se dice lo que pasó, no lo que se quería que pasara. Anunciar «enviada»
      // cuando el correo no salió deja a alguien esperando un mail que no
      // existe, y sin motivo para sospechar.
      if (r.resultado === 'enviada') {
        setMsg(`Invitación enviada por correo a ${email}`);
      } else if (r.resultado === 'ya_tenia_cuenta') {
        // Ni verde de «listo» ni rojo de «falló»: salió bien, pero hay algo
        // que hacer. A quien ya tiene cuenta no le llega ningún correo, así
        // que si nadie le avisa, la invitación se queda esperando para
        // siempre.
        setAviso(
          `${email} ya tiene cuenta, así que no le llega ningún correo. La invitación le va a ` +
            `aparecer en Inicio apenas abra la aplicación: avisale que entre y toque «Unirme».`,
        );
      } else {
        setAviso(
          `La invitación quedó creada para ${email}, pero el correo no se pudo enviar ` +
            `(${r.motivo}). Avisale por otro medio: cuando entre con ese mismo correo, ` +
            `va a ver la invitación esperándola.`,
        );
      }
      setEmail('');
      setParticipante('');
      // Ese participante queda tomado en cuanto acepte: se saca de la lista.
      setRecarga((n) => n + 1);
    } catch (err) {
      setError(mensajeDeError(err));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* La pantalla se llamaba por su primera tarjeta: entraba directo en
          «Invitar a alguien», que es lo que MENOS se hace acá. El encabezado
          dice de qué pantalla se trata, y la invitación pasa a ser una sección
          más. */}
      <EncabezadoPantalla titulo="Equipo" nota="Quiénes entran y quiénes se reparten la plata" />

      <section className="flex flex-col gap-3" data-testid="team-page">
        <EncabezadoSeccion titulo="Invitar a alguien" />
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input
            type="email"
            required
            placeholder="correo@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
            data-testid="invite-email"
          />
          <Chips
            value={role}
            onChange={setRole}
            options={[
              { value: 'member' as const, label: 'Miembro' },
              { value: 'admin' as const, label: 'Administrador' },
            ]}
            testId="invite-role"
          />

          {/* A quién corresponde en el reparto.
              Se pregunta ACÁ porque es el único momento en que quien invita
              tiene presente a quién está invitando. Sin esta pregunta, alguien
              que cobra en los repartos desde hace meses entra y lee «No
              participás económicamente en este período»: la base no tiene cómo
              saber que ese usuario es ese nombre, y RLS le oculta hasta sus
              propias líneas.
              Sólo aparece si hay a quién elegir: en una veterinaria donde
              todos los participantes ya tienen usuario, este campo sería un
              desplegable con una sola opción vacía. */}
          {libres.length > 0 && (
            <label className="flex flex-col gap-1 text-sm">
              ¿Quién es en el reparto?
              <select
                value={participante}
                onChange={(e) => setParticipante(e.target.value)}
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] bg-[var(--superficie)] px-2 text-base"
                data-testid="invite-participante"
              >
                <option value="">Todavía no está en el reparto</option>
                {libres.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--tinta-tenue)]">
                Si ya cobra en los repartos, elegilo: es lo que le permite ver su propia ganancia y
                su saldo. Si todavía no cobra nada, dejalo como está y se puede vincular después.
              </span>
            </label>
          )}

          {error && <p className="text-sm text-[var(--peligro)]">{error}</p>}
          {msg && (
            <p className="text-sm text-[var(--exito)]" data-testid="invite-ok">
              {msg}
            </p>
          )}
          {aviso && (
            <p
              role="alert"
              className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
              data-testid="invite-sin-aviso"
            >
              {aviso}
            </p>
          )}
          <button
            type="submit"
            className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-[var(--sobre-accion)]"
            data-testid="invite-submit"
          >
            Invitar
          </button>
        </form>
      </section>

      {/* Administrar participantes no existía: había que crearlos con SQL. */}
      <ParticipantesCard
        organizationId={activeOrg.organization_id}
        isAdmin={activeOrg.is_admin ?? false}
      />
    </div>
  );
}
